import { CardContent } from '@invent/wl-ui-kit-next/ui';

import type { IOversightResponseRow } from '../../api/oversight-contracts';
import { Pager, PastTheEnd, Section } from '../../components/admin-page';
import { ResponsesTable } from '../../components/responses-table';
import { useClientPage } from './client-page';

/**
 * The household's responses, any status, newest change first — the hub document's
 * `responses`, drawn by the responses list's own table, and paged here (`useClientPage`). Its
 * participant column is the household member a response is for, drawn where the household has
 * more than one and left out where every row would repeat the page's own title. A row opens the
 * response, as it does on the list. Nothing is read: the rows are the hub's, so they are exactly
 * as current as the page.
 *
 * **Cycles as sent** (R10): a migrated participant's legacy years arrive as cycles of their own
 * (`legacy-2021`, …), one response each, and are rows like any other — grouping them by survey
 * here would be a second opinion on what a cycle is. Progress is the shared table's column, so
 * the responses list shows it too.
 */
export function SurveysSection({
  responses,
  multi,
  anchor,
  title = 'Surveys'
}: {
  responses: IOversightResponseRow[];
  multi: boolean;
  anchor: string;
  /** "Responses" on a person's survey tab (SR-391), which lists that survey's alone. */
  title?: string;
}) {
  const { rows, setPage, more, pager } = useClientPage(responses);

  return (
    <Section title={title} anchor={anchor}>
      <CardContent className="grid gap-4">
        <ResponsesTable
          rows={rows}
          empty={
            pager.total > 0 ? (
              <PastTheEnd {...pager} counted={['response', 'responses']} onPage={setPage} />
            ) : (
              'No response is held for this participant.'
            )
          }
          participantColumn={multi}
          dates
          maxHeight="none"
        />
        <Pager {...pager} onPage={setPage} quiet onMore={more} />
      </CardContent>
    </Section>
  );
}
